import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { SupporterChatRoomItem } from './SupporterChatRoomItem';
import { SupporterChatRoom } from './SupporterChatRoom';
import { useContext } from 'react';
import { supporterChatContext } from 'application/contexts/useSupporterChatRoom';
import { PageTitle } from 'ui/components/elements/PageTitle/PageTitle';
import { mqUser, mqMin } from 'utils/responsive';
import { ISupporterChatRoom } from 'domain/entities/ChatRoom/SupporterChatRoom';
import { useParams } from 'react-router-dom';

const Wrapper = styled.div`
    font-size: var(--font-medium);
    margin: 0 auto;
    background-color: #f8f9fd;
    padding: 10px 20px;
`;

const FlexContainer = styled.div`
    display: flex;
    width: 100%;

    ${mqUser('SP')} {
        flex-direction: column;
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    overflow-y: auto;
    width: 100%;
    justify-content: space-between;
`;

const ListContainer = styled(Container)`
    flex-basis: 30%;
    min-height: calc(100vh - 150px);
    justify-content: flex-start;
    overflow-y: auto;
    padding-right: 15px;

    ${mqUser('SP')} {
        padding-right: 0;
        display: ${({ $isHidden }: { $isHidden?: boolean }) => ($isHidden ? 'none' : 'flex')};
    }
`;

const ChatRoomContainer = styled(Container)`
    flex-basis: 69%;
    background-color: #fff;
    height: calc(100vh - 150px);

    ${mqUser('SP')} {
        display: ${({ $isHidden }: { $isHidden?: boolean }) => ($isHidden ? 'none' : 'flex')};
        height: calc(100vh - 80px);
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        z-index: 1000;
    }
`;

const SearchInput = styled.input`
    padding: 10px;
    border: 1px solid #5f5f5f;
    border-radius: 1px;
    font-size: var(--font-medium);

    &:focus {
        outline: none;
    }
`;

const NoMessage = styled.div`
    font-size: var(--font-medium);
    color: #5f5f5f;
    text-align: center;
    margin-top: 20px;
`;

const ChatItemsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;

    ${mqMin('NARROW')} {
        height: calc(100% - 240px);
    }
`;

export const SupporterChatRoomList: React.FC = () => {
    const {
        filteredChatRooms,
        selectedChatRoom,
        unreadIds,
        setSelectedChatRoom,
        keyword,
        setKeyword,
        fetchData,
        loading,
        fetchChatMessages,
        chatRooms,
    } = useContext(supporterChatContext);

    const [isSP] = useState(window.innerWidth <= 799);
    const [showChatRoom, setShowChatRoom] = useState(false);
    const params = useParams<{ chat_room_id?: string }>();

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // URLパラメータからチャットルームIDを取得して選択
    useEffect(() => {
        if (params.chat_room_id === undefined) return;
        const chatRoomId = Number(params.chat_room_id);
        if (!chatRoomId) return;
        const selected = chatRooms.find((chatRoom) => chatRoom.id === chatRoomId);
        if (selected) {
            setSelectedChatRoom(selected);
            fetchChatMessages(chatRoomId);
            if (isSP) {
                setShowChatRoom(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.chat_room_id]);

    const handleChatRoomSelect = (chatRoom: ISupporterChatRoom) => {
        setSelectedChatRoom(chatRoom);
        fetchChatMessages(chatRoom.id);
        if (isSP) {
            setShowChatRoom(true);
        }
    };

    const handleBackToList = () => {
        if (isSP) {
            setShowChatRoom(false);
        }
    };

    return (
        <Wrapper>
            <PageTitle title="メッセージ" />
            <FlexContainer>
                <ListContainer $isHidden={isSP && showChatRoom}>
                    <h4>企業を検索する</h4>
                    <SearchInput
                        placeholder="企業名で検索"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                    />
                    <ChatItemsContainer>
                        {filteredChatRooms.map((chatRoom) => (
                            <SupporterChatRoomItem
                                key={chatRoom.id}
                                chatRoom={chatRoom}
                                unread={unreadIds.includes(chatRoom.id)}
                                selected={selectedChatRoom?.id === chatRoom.id}
                                onClick={() => handleChatRoomSelect(chatRoom)}
                            />
                        ))}
                    </ChatItemsContainer>
                </ListContainer>
                <ChatRoomContainer $isHidden={isSP && !showChatRoom}>
                    {filteredChatRooms.length === 0 && !loading ? (
                        <NoMessage>まだメッセージがありません。</NoMessage>
                    ) : selectedChatRoom ? (
                        <SupporterChatRoom onBack={handleBackToList} />
                    ) : (
                        <NoMessage>左のメニューからチャットルームを選択してください。</NoMessage>
                    )}
                </ChatRoomContainer>
            </FlexContainer>
        </Wrapper>
    );
};
